(ns frontend.actions.entity
  (:require
   [clojure.string :as string]
   [frontend.conn :as conn]
   [frontend.state.current-session :as session]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [promesa.core :as p]))

(defn *->disable!
  [entity-id on-success! on-error!]
  (-> (conn/sendp! :entity/disable!
                   {:entity-id (or (:db/id entity-id) entity-id)})
      (p/then on-success!)
      (p/catch on-error!)))

(defn *->disable-raw!
  [msg on-success! on-error!]
  (-> (conn/sendp! :entity/disable!
                   msg)
      (p/then on-success!)
      (p/catch on-error!)))

(defn *->field-update!
  [entity-id attribute value                                ;; if value = nil -> remove attribute
   on-success! on-error!]
  (swap! session/pending-updates assoc-in [entity-id attribute] value)

  (-> (conn/sendp! (if (or (nil? value)
                           (and (string? value)
                                (empty? (string/trim value))))
                     :entity/remove-attribute
                     :entity/update-attribute)
                   {:db/id     entity-id
                    :attribute attribute
                    :value     value})
      (p/then (fn [resp]
                (swap! session/pending-updates potpuri/dissoc-in [entity-id attribute])
                (on-success! resp)))
      (p/catch (fn [err]
                 (swap! session/pending-updates potpuri/dissoc-in [entity-id attribute])
                 (swap! session/failed-updates assoc-in [entity-id attribute] value)
                 (on-error! err)))))

(defn *->field-update-instant-indexing!
  [entity-id attribute value                                ;; if value = nil -> remove attribute
   on-success! on-error!]
  (swap! session/pending-updates assoc-in [entity-id attribute] value)

  (-> (conn/sendp! (if (or (nil? value)
                           (and (string? value)
                                (empty? (string/trim value))))
                     :entity/remove-attribute
                     :entity/update-attribute)
                   {:db/id             entity-id
                    :attribute         attribute
                    :value             value
                    :instant-indexing? true})
      (p/then (fn [resp]
                (swap! session/pending-updates potpuri/dissoc-in [entity-id attribute])
                (on-success! resp)))
      (p/catch (fn [err]
                 (swap! session/pending-updates potpuri/dissoc-in [entity-id attribute])
                 (swap! session/failed-updates assoc-in [entity-id attribute] value)
                 (on-error! err)))))

(defn optimistic-field-update
  [entity-id attribute new-value entities-atom on-success! on-error!]
  (let [old-value (get-in @entities-atom [entity-id attribute])]
    ;;optimistic update:
    (swap! entities-atom assoc-in [entity-id attribute] new-value)
    (deref entities-atom)

    (*->field-update!
     entity-id
     attribute
     new-value
     on-success!
     (fn [e]
       ;;retract optimistic update:
       (when (= new-value (get-in @entities-atom [entity-id attribute]))
         (swap! entities-atom assoc-in [entity-id attribute] old-value)
         (deref entities-atom))

       (when on-error!
         (on-error! e))))))

(defn *->entity-update!
  [entity-id updates-map                                    ;; if value = nil -> remove attribute
   on-success! on-error!]
  (-> (conn/sendp! :entity/update-attributes
                   {:db/id             entity-id
                    :data              updates-map
                    :instant-indexing? true})
      (p/then on-success!)
      (p/catch on-error!)))

(defn *->move-to-stage!
  "Wires the entry-criteria modal submit to the backend
   `:entity/move-to-stage` handler. `body` matches `MoveToStageRequest`:

       {:opportunity     {:db/id ..., :entity/type ..., <field-edits>}
        :target-stage-id <stage-id>
        :target          {...optional field-edits...}
        :target-profile  {...optional field-edits...}
        :associations    {...optional new-association edits...}}

   No sync wait around the response — callers decide between optimistic
   UI updates (apply locally before issuing the call) and waiting for the
   sync to land (`session/wait-for-entity-update!` inside their own
   `on-success!`). Also leaves room for a future no-stage-change variant
   that submits only fields/associations — the opportunity may not be
   touched at all, so an unconditional wait would deadlock."
  [body on-success! on-error!]
  (-> (conn/sendp! :entity/move-to-stage body)
      (p/then (fn [resp]
                (when (fn? on-success!)
                  (on-success! resp))))
      (p/catch (fn [err]
                 (when (fn? on-error!)
                   (on-error! err))))))

(defn *->attempt-move-to-stage!
  "Validated stage-move: unlike `*->move-to-stage!`, the backend resolves
   entry-criteria itself (against live Datomic state - no client-side
   `@session/entities` needed) and either moves the opportunity or refuses,
   returning which fields/associations are missing instead of transacting.
   `body` is the same `MoveToStageRequest` shape as `*->move-to-stage!`, with
   an optional `:criteria-reviewed?` that the entry-criteria modal sets on its
   own submit.

   Goes over `conn/sendp-rpc!` (the `/rpc` HTTP transport,
   `backend.transport.core/execute-rpc`), not `conn/sendp!` - this endpoint
   is registered via `backend.endpoint/register-endpoints`, a different
   dispatch path than the legacy per-domain `handlers` map `sendp!`'s eines
   transport routes against.

   `on-success!` is called with the raw response - `{:status :moved
   :opportunity ...}`, `{:status :missing-criteria :criteria [...]}`,
   `{:status :review :review {...}}` or `{:status :missing-target}` -
   callers branch on `:status`, not on whether the call succeeded."
  [body on-success! on-error!]
  (-> (conn/sendp-rpc! :entity/attempt-move-to-stage body)
      (p/then (fn [resp]
                (when (fn? on-success!)
                  ;; execute-rpc wraps the endpoint's own return value in
                  ;; {:body ...}; unwrap so callers see {:status ...} directly.
                  (on-success! (:body resp)))))
      (p/catch (fn [err]
                 (when (fn? on-error!)
                   (on-error! err))))))

(defn *->entities-update!
  "Multi-entity field update in a single round-trip. `entity-updates` is a vector
   of {:db/id id :data {attr value ...}}. Each entity is updated in its own backend
   transaction (atomic per entity; no cross-entity atomicity), all in one request.
   nil values retract the attribute."
  [entity-updates on-success! on-error!]
  (-> (conn/sendp! :entity/update-entities
                   (mapv #(assoc % :instant-indexing? true) entity-updates))
      (p/then on-success!)
      (p/catch on-error!)))

(defn *->field-update-with-new-entity!
  [entity-id attribute value
   new-entity-params-str
   on-success! on-error!]
  (-> (conn/sendp! :entity/update-attribute-with-new-entity
                   {:db/id          entity-id
                    :attribute      attribute
                    :value          value
                    :new-entity-str new-entity-params-str})
      (p/then on-success!)
      (p/catch on-error!)))

; Association target and source direction is resolved on backend
(defn *->add-association
  [entity-id
   association-type
   target-entity-type
   target-id
   new-option-string
   source-comment
   target-comment
   on-success!
   on-error!]
  (-> (conn/sendp! :entity/add-association
                   {:source-id          entity-id
                    :association-type   association-type
                    :target-entity-type target-entity-type
                    :target-id          target-id
                    :source-comment     source-comment
                    :target-comment     target-comment
                    :new-option         new-option-string})
      (p/then on-success!)
      (p/catch on-error!)))

(defn *->mass-add-associations
  [data on-success! on-error!]
  (-> (conn/sendp! :entity/mass-add-associations
                   data)
      (p/then on-success!)
      (p/catch on-error!)))

(defn *->remove-association
  [entity-id association-id on-success! on-error!]
  (-> (conn/sendp! :entity/remove-association
                   {:entity-id      entity-id
                    :association-id association-id})
      (p/then on-success!)
      (p/catch on-error!)))

(defn *->create
  [data on-success! on-error!]
  (-> (conn/sendp! :entity/create-entity
                   data
                   120000)
      (p/then (fn [{:keys [body] :as resp}]
                (if (:db/id body)
                  (on-success! resp)
                  (on-error! resp))))
      (p/catch on-error!)))

(defn *->bulk-edit
  [data on-success! on-error!]
  (-> (conn/sendp! :entity/bulk-edit!
                   data
                   120000)
      (p/then on-success!)
      (p/catch on-error!)))

(defn *->bulk-put-on-hold
  [data on-success! on-error!]
  (-> (conn/sendp! :entity/bulk-put-on-hold!
                   data
                   120000)
      (p/then on-success!)
      (p/catch on-error!)))

(defn *->bulk-pass
  [data on-success! on-error!]
  (-> (conn/sendp! :entity/bulk-pass!
                   data
                   120000)
      (p/then on-success!)
      (p/catch on-error!)))

(defn *->find-matches
  [data on-success! on-error!]
  (-> (conn/sendp! :entity/find-matches
                   data
                   120000)
      (p/then on-success!)
      (p/catch on-error!)))

(defn *->get-display-strings
  [data on-success! on-error!]
  (-> (conn/sendp! :entity/get-display-strings
                   data
                   120000)
      (p/then on-success!)
      (p/catch on-error!)))

(rf/reg-fx
 ::*->entity-update!
 (fn [{:keys [entity-id updates-map on-success on-error] :as _args}]
   (*->entity-update!
    entity-id
    updates-map
    (fn [success-resp]
      (rf/dispatch (conj on-success success-resp)))
    (fn [error-resp]
      (rf/dispatch (conj on-error error-resp))))))
